import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import productThumb1 from "@/assets/product-video-thumb1.jpg";
import productThumb2 from "@/assets/product-video-thumb2.jpg";

interface DropdownItem {
  to: string;
  label: string;
  description?: string;
}

interface MegaMenuColumn {
  heading?: string;
  items: DropdownItem[];
}

interface VideoCard {
  image: string;
  category: string;
  title: string;
  action: string;
  to: string;
}

interface ArticleCard {
  image: string;
  date: string;
  title: string;
  description: string;
  to: string;
}

interface MegaMenuConfig {
  columns: MegaMenuColumn[];
  videos?: VideoCard[];
  articles?: ArticleCard[];
}

const productMenu: MegaMenuConfig = {
  columns: [
    {
      heading: "Platform",
      items: [
        { to: "/clinic-genius-ai", label: "Clinic Genius AI", description: "Front Desk OS for clinics" },
        { to: "/clinarium", label: "Clinarium AI", description: "Clinical appointment recovery and risk intelligence" },
        { to: "/voice-agent-builder", label: "AI receptionist builder", description: "Build an AI receptionist and call it live" },
      ],
    },
    {
      heading: "Services",
      items: [
        { to: "/products/voice", label: "Astreon Voice", description: "Conversational voice agents" },
        { to: "/products/flow", label: "Astreon Flow", description: "Workflow automation" },
        { to: "/products/insights", label: "Astreon Insights", description: "Decision intelligence" },
        { to: "/products/engage", label: "Astreon Engage", description: "Customer engagement at scale" },
        { to: "/products/custom", label: "Astreon Custom", description: "Bespoke AI builds" },
      ],
    },
  ],
};

const solutionMenu: MegaMenuConfig = {
  columns: [
    {
      heading: "Industries",
      items: [
        { to: "/industries/construction", label: "Construction", description: "Build better, faster" },
        { to: "/industries/ecommerce", label: "E-commerce", description: "Sell smarter, scale faster" },
        { to: "/industries/education", label: "Education", description: "Personalised learning at scale" },
        { to: "/industries/finance", label: "Finance", description: "Precision at scale" },
        { to: "/industries/healthcare", label: "Healthcare", description: "Smarter patient care" },
      ],
    },
    {
      items: [
        { to: "/industries/hospitality", label: "Hospitality", description: "Exceptional guest experiences" },
        { to: "/industries/insurance", label: "Insurance", description: "Faster claims, smarter risk" },
        { to: "/industries/it-enterprises", label: "IT enterprises", description: "Operations that run themselves" },
        { to: "/industries/law-firms", label: "Law Firms", description: "Legal work, redrafted" },
        { to: "/industries/logistics", label: "Logistics", description: "Optimise your supply chain" },
        { to: "/industries/manufacturing", label: "Manufacturing", description: "Intelligent operations & automation" },
      ],
    },
    {
      items: [
        { to: "/industries/real-estate", label: "Real estate", description: "Close deals faster" },
        { to: "/industries/recruitment", label: "Recruitment", description: "Faster placements, better matches" },
        { to: "/industries/retail", label: "Retail", description: "Reimagine the retail experience" },
        { to: "/industries/travel-tourism", label: "Travel & Tourism", description: "Unforgettable journeys, intelligently delivered" },
      ],
    },
  ],
};

const newsMenu: MegaMenuConfig = {
  columns: [
    {
      heading: "Insights",
      items: [
        { to: "/blog", label: "Blog", description: "Latest thinking and analysis" },
        { to: "/newsletters", label: "Newsletters", description: "Five publications, one editorial standard" },
        { to: "/reels", label: "Film library", description: "Short films on AI in practice" },
      ],
    },
  ],
};

const aboutMenu: MegaMenuConfig = {
  columns: [
    {
      heading: "About us",
      items: [
        { to: "/about/who-we-are", label: "Who we are", description: "The story behind Astreon" },
        { to: "/about/leadership", label: "Our Leadership", description: "Meet the people leading us" },
        { to: "/about/team", label: "Meet the Team", description: "The people behind the work" },
        { to: "/about/purpose", label: "Purpose, Mission & Values", description: "What drives our work" },
      ],
    },
    {
      items: [
        { to: "/about/history", label: "Our History", description: "Milestones and moments" },
        { to: "/about/aspiration", label: "Our Aspiration", description: "Where we're heading" },
        { to: "/about/governance", label: "Our Governance", description: "How we operate responsibly" },
      ],
    },
  ],
};

const loginMenu: MegaMenuConfig = {
  columns: [
    {
      heading: "Get started",
      items: [
        { to: "/book-a-demo", label: "Book a complimentary audit", description: "See Astreon in action" },
        { to: "/contact-us", label: "Contact Us", description: "Speak with our team" },
      ],
    },
  ],
};

export const megaMenus: Record<string, MegaMenuConfig> = {
  Products: productMenu,
  Solutions: solutionMenu,
  "About us": aboutMenu,
  News: newsMenu,
  Login: loginMenu,
};

interface MegaMenuPanelProps {
  config: MegaMenuConfig;
  onClose: () => void;
}

const MegaMenuPanel = ({ config, onClose }: MegaMenuPanelProps) => {
  const hasVideos = config.videos && config.videos.length > 0;
  const hasArticles = config.articles && config.articles.length > 0;
  const hasRightPanel = hasVideos || hasArticles;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.2, ease: [0.25, 0.4, 0.25, 1] }}
        className="absolute left-0 right-0 top-full z-50 bg-background border-b border-border"
        onMouseLeave={onClose}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
          <div className={`grid gap-10 ${hasRightPanel ? 'grid-cols-[auto_1fr]' : 'grid-cols-1 max-w-[700px]'}`}>
            {/* Left side: Links in columns */}
            <div className={`grid ${config.columns.length >= 3 ? 'grid-cols-3' : config.columns.length === 2 ? 'grid-cols-2' : 'grid-cols-1'} gap-x-16 gap-y-0`}>
              {config.columns.map((col, colIdx) => (
                <div key={colIdx}>
                  {col.heading && (
                    <p className="text-[12px] text-muted-foreground mb-5 text-body tracking-wide">
                      {col.heading}
                    </p>
                  )}
                  {!col.heading && <div className="h-[17px] mb-5" />}
                  <div className="space-y-4">
                    {col.items.map((item) => (
                      <Link
                        key={item.label}
                        to={item.to}
                        onClick={onClose}
                        className="block group"
                      >
                        <span className="text-[16px] font-semibold text-foreground group-hover:text-foreground/60 transition-colours text-display block leading-snug">
                          {item.label}
                        </span>
                        {item.description && (
                          <span className="text-[13px] text-muted-foreground text-body block mt-0.5">
                            {item.description}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Right side: Video cards */}
            {hasVideos && (
              <div>
                <p className="text-[12px] text-muted-foreground mb-5 text-body tracking-wide">
                  Product videos
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {config.videos!.map((video, i) => (
                    <Link
                      key={i}
                      to={video.to}
                      onClick={onClose}
                      className="group block"
                    >
                      <div className="aspect-[5/4] rounded-lg overflow-hidden mb-3 bg-muted">
                        <img
                          src={video.image}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground text-body mb-1">
                        {video.category}
                      </p>
                      <p className="text-[15px] font-semibold text-foreground text-display leading-snug mb-1">
                        {video.title}
                      </p>
                      <p className="text-[13px] text-muted-foreground text-body underline underline-offset-2">
                        {video.action}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Right side: Article cards */}
            {hasArticles && (
              <div>
                <p className="text-[12px] text-muted-foreground mb-5 text-body tracking-wide">
                  Latest
                </p>
                <div className="grid grid-cols-3 gap-5">
                  {config.articles!.map((article, i) => (
                    <Link
                      key={i}
                      to={article.to}
                      onClick={onClose}
                      className="group block"
                    >
                      <div className="aspect-[4/5] rounded-lg overflow-hidden mb-3 bg-muted">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground text-body mb-1.5">
                        {article.date}
                      </p>
                      <p className="text-[15px] font-semibold text-foreground text-display leading-snug mb-1.5">
                        {article.title}
                      </p>
                      <p className="text-[13px] text-muted-foreground text-body leading-relaxed line-clamp-3">
                        {article.description}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MegaMenuPanel;
